import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ArrowLeft, Send, Tag, UserCheck, Clock, Plus, X } from 'lucide-react';

const STAGES = ['new_inquiry','qualified_review','contacted','meeting_booked','discovery_completed','proposal','negotiation','won','lost','archived'];
const STAGE_LABELS = { new_inquiry:'New Inquiry', qualified_review:'Qualified Review', contacted:'Contacted', meeting_booked:'Meeting Booked', discovery_completed:'Discovery Completed', proposal:'Proposal', negotiation:'Negotiation', won:'Won', lost:'Lost', archived:'Archived' };
const TIER_CLASSES = { A:'tier-a', B:'tier-b', C:'tier-c', D:'tier-d', E:'tier-e' };
const OWNERS = ['David', 'Carlo'];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);

  const loadLead = useCallback(async () => {
    try {
      await api.adminVerify();
      const r = await api.getLead(id);
      setLead(r.data);
    } catch { navigate('/admin'); }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => { loadLead(); }, [loadLead]);

  const changeStage = async (stage) => {
    try { await api.updateStage(id, stage); loadLead(); } catch {}
  };
  const assignOwner = async (owner) => {
    try { await api.assignLead(id, owner); loadLead(); } catch {}
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.addNote(id, { content: noteText }); setNoteText(''); loadLead(); } catch {}
  };
  const addTag = async () => {
    if (!tagInput.trim()) return;
    try { await api.addTag(id, tagInput.trim()); setTagInput(''); loadLead(); } catch {}
  };
  const removeTag = async (tag) => {
    try { await api.removeTag(id, tag); loadLead(); } catch {}
  };
  const togglePriority = async () => {
    const newP = lead.priority === 'urgent' ? 'normal' : 'urgent';
    try { await api.updateLead(id, { priority: newP }); loadLead(); } catch {}
  };

  if (loading) return <div className="min-h-screen bg-aurocle-base flex items-center justify-center"><div className="text-aurocle-gold text-sm font-mono animate-pulse">Loading...</div></div>;
  if (!lead) return null;

  return (
    <div className="min-h-screen bg-aurocle-base p-6 lg:p-8" data-testid="lead-detail-page">
      <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-2 text-xs text-aurocle-text-secondary hover:text-aurocle-gold transition-colors mb-6" data-testid="back-to-dashboard">
        <ArrowLeft size={14} /> Back to Dashboard
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-aurocle-surface border border-white/5 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="font-heading text-xl text-aurocle-text">{lead.full_name}</h1>
                <p className="text-sm text-aurocle-text-secondary">{lead.company_name} {lead.job_title && `— ${lead.job_title}`}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`pill text-xs ${TIER_CLASSES[lead.qualification_tier]}`}>Tier {lead.qualification_tier}</span>
                {lead.enterprise_flag && <span className="pill pill-gold text-xs">Enterprise</span>}
                <button onClick={togglePriority} data-testid="toggle-priority"
                  className={`pill text-xs cursor-pointer ${lead.priority === 'urgent' ? 'pill-warning' : 'pill text-aurocle-text-secondary'}`}>
                  {lead.priority === 'urgent' ? 'URGENT' : 'Normal'}
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {[
                ['Email', lead.email], ['Phone', lead.phone], ['Country', lead.country],
                ['Company Type', lead.company_type], ['Team Size', lead.team_size], ['Interest', lead.main_interest],
                ['Urgency', lead.urgency], ['Entities', lead.num_entities], ['Contact Method', lead.preferred_contact],
                ['Source', lead.source], ['Score', lead.qualification_score],
              ].filter(([,v]) => v).map(([label, value]) => (
                <div key={label}>
                  <span className="text-xs font-mono text-aurocle-text-secondary uppercase tracking-wider">{label}</span>
                  <div className="text-aurocle-text mt-0.5">{value}</div>
                </div>
              ))}
            </div>
            {lead.message && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <span className="text-xs font-mono text-aurocle-text-secondary uppercase tracking-wider">Message</span>
                <p className="text-sm text-aurocle-text mt-1 leading-relaxed">{lead.message}</p>
              </div>
            )}
          </div>

          <div className="bg-aurocle-surface border border-white/5 p-6">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-4">Notes</h3>
            <div className="flex gap-3 mb-4">
              <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..."
                className="premium-input flex-1 text-xs" data-testid="note-input" onKeyDown={(e) => e.key === 'Enter' && addNote()} />
              <button onClick={addNote} className="btn-gold text-xs py-2 px-4" data-testid="add-note-btn"><Send size={12} /></button>
            </div>
            <div className="space-y-3">
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="p-3 bg-aurocle-base/50 border border-white/3">
                  <p className="text-sm text-aurocle-text">{n.content}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-aurocle-text-secondary">
                    <span>{n.created_by}</span>
                    <span>{n.created_at?.split('T')[0]} {n.created_at?.split('T')[1]?.substring(0,5)}</span>
                  </div>
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <p className="text-xs text-aurocle-text-secondary">No notes yet.</p>}
            </div>
          </div>

          <div className="bg-aurocle-surface border border-white/5 p-6">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-4">Timeline</h3>
            <div className="space-y-3">
              {(lead.timeline || []).map((t) => (
                <div key={t.id} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-aurocle-gold/50 mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-aurocle-text">{t.description}</p>
                    <div className="text-[10px] font-mono text-aurocle-text-secondary mt-0.5">
                      {t.created_by} — {t.created_at?.split('T')[0]} {t.created_at?.split('T')[1]?.substring(0,5)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-aurocle-surface border border-white/5 p-5">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">Pipeline Stage</h3>
            <select value={lead.pipeline_stage} onChange={(e) => changeStage(e.target.value)}
              className="premium-select text-xs" data-testid="stage-select">
              {STAGES.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </div>

          <div className="bg-aurocle-surface border border-white/5 p-5">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">
              <UserCheck size={12} className="inline mr-1" /> Owner
            </h3>
            <select value={lead.owner || ''} onChange={(e) => assignOwner(e.target.value)}
              className="premium-select text-xs" data-testid="owner-select">
              <option value="">Unassigned</option>
              {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>

          <div className="bg-aurocle-surface border border-white/5 p-5">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">
              <Tag size={12} className="inline mr-1" /> Tags
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(lead.tags || []).map((t) => (
                <span key={t} className="pill text-[10px] group cursor-pointer" onClick={() => removeTag(t)}>
                  {t} <X size={8} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              ))}
              {(!lead.tags || lead.tags.length === 0) && <span className="text-xs text-aurocle-text-secondary">No tags</span>}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag"
                className="premium-input flex-1 text-xs" data-testid="tag-input" onKeyDown={(e) => e.key === 'Enter' && addTag()} />
              <button onClick={addTag} className="btn-outline text-xs py-1.5 px-3" data-testid="add-tag-btn"><Plus size={10} /></button>
            </div>
          </div>

          <div className="bg-aurocle-surface border border-white/5 p-5">
            <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">
              <Clock size={12} className="inline mr-1" /> Activity
            </h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-aurocle-text-secondary">Created</span><span className="font-mono text-aurocle-text">{lead.created_at?.split('T')[0]}</span></div>
              <div className="flex justify-between"><span className="text-aurocle-text-secondary">Updated</span><span className="font-mono text-aurocle-text">{lead.updated_at?.split('T')[0]}</span></div>
              {lead.last_contacted && <div className="flex justify-between"><span className="text-aurocle-text-secondary">Last Contact</span><span className="font-mono text-aurocle-text">{lead.last_contacted}</span></div>}
            </div>
          </div>

          {lead.bookings && lead.bookings.length > 0 && (
            <div className="bg-aurocle-surface border border-white/5 p-5">
              <h3 className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">Bookings</h3>
              {lead.bookings.map((b) => (
                <div key={b.id} className="p-3 bg-aurocle-base/50 border border-white/3 mb-2">
                  <div className="text-xs text-aurocle-text">{b.consultation_type}</div>
                  <div className="text-[10px] font-mono text-aurocle-text-secondary mt-1">{b.meeting_status} — {b.created_at?.split('T')[0]}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
